import { MODALS_ENUM } from "@constants/Modals";
import { AddLocation_RequestTypes, DeleteLocation_RequestTypes, GetLocations_RequestTypes } from "@mobx/api/locations";
import apiService, { createStore, RequestControl, withLoading } from "@mobx/services/http";
import { checkingLocationsPositions3, saveLocationsPos3 } from "@mobx/utils/saveLocationsPositions";
import store_ from "@store";
import { entries } from "@utils/entries";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { show } from "redux-modal";
import { getGlobalIntl } from "../../../i18n/ProviderIntl";
import { IMainLocationsStore, IMainLocationsStoreBase, LocationStore, MainLocationsStore } from "./interfaces";
import { createLocationStore } from "./location";
import { ILocation3, ILocationStringsData } from "./types";


const createMainLocationsStore_ = (rc: RequestControl): IMainLocationsStore => {
  const getLocations = apiService.createRequest(GetLocations_RequestTypes);
  const deleteLocation = apiService.createRequest(DeleteLocation_RequestTypes);
  const addLocation = apiService.createRequest(AddLocation_RequestTypes)

  const self: IMainLocationsStoreBase = {
    isLoading: true,
    isInitialized: false,
    locations: {} as Record<number, LocationStore>,
    locationsOrder: [],
    minersLog: [],
    activeLocation: undefined,

    async setActiveLocation(locationId?: number) {
      if (locationId === self.activeLocation?.location.Id) return;

      for (const [id, store] of entries(self.locations)) {
        const active = id === `${locationId}`;
        await store.setActive(active);
        if (active) runInAction(() => {
          self.activeLocation = store;
        });
      }
      if (locationId === undefined) {
        runInAction(() => {
          self.activeLocation = undefined;
        });
      }
    },

    get locationsInOrder() {
      return self.locationsOrder.map(id => self.locations[id]);
    },

    addMinersLog(id: number, prev: string, next: string) {
      self.minersLog = [...self.minersLog, {id, prev, next}];
    },

    showAddLocationModal() {
      store_()?.dispatch(show(MODALS_ENUM.ADD_LOCAION_MOBX));
    },

    deleteLocation(id: number) {
      const { formatMessage } = getGlobalIntl();
      return new Promise<boolean>(resolve => {
        const onConfirm = async () => {
          const response = await withLoading(self, deleteLocation(rc.createRequestHandle(), id));
          if (!response) {
            resolve(false)
            return;
          }

          const {[id]: _, ...rest} = self.locations;
          runInAction(() => {
            self.locations = rest;
            self.locationsOrder = self.locationsOrder.filter(locID => locID !== id);
            if (self.activeLocation?.location.Id === id) self.activeLocation = undefined;
          })
          resolve(true);
        };

        store_()?.dispatch(
          show(MODALS_ENUM.CONFIRM_MODAL, {
            title: " ",
            subtitle: formatMessage({ id: "locations.delete_confirm" }),
            onConfirm,
          })
        );
      });
    },

    async addLocation(data: ILocationStringsData) {
      const r = await addLocation(rc.createRequestHandle(), data);
      if (!r) return false;
      const loc: ILocation3 = r.Record;
      const r2 = await createLocationStore(loc, self);
      if (!r2) return false;

      runInAction(() => {
        self.locations = {...self.locations, [loc.Id]: r2};
        self.locationsOrder = [...self.locationsOrder, loc.Id]
      })

      return true;
    },

    checkPositionsOfLocations(userEmail?: string) {
      if (self.locationsInOrder.length > 0 && userEmail) {
        const locationWithPositions = checkingLocationsPositions3(
          userEmail,
          self.locationsInOrder.map(store => store.location)
        );
        self.locationsOrder = locationWithPositions.map(loc => loc.Id);
      }
    },

    editPositionsOfLocations(ids: number[], userEmail?: string) {
      if (!userEmail) return;
      self.locationsOrder = ids;
      saveLocationsPos3(ids, userEmail);
    },

    async init() {
      self.isInitialized = false;
      self.locations = {} as Record<number, LocationStore>;
      self.locationsOrder = [];
      const active = self.activeLocation?.location.Id;
      self.activeLocation = undefined;
      
      const res = await withLoading(self, getLocations(rc.createRequestHandle()));
      if (!res || res.Status !== "success" || !res.Records) {
        return;
      }

      for (const rec of res.Records) {
        const loc = rec as unknown as ILocation3;
        loc.Id = +rec.Id
        const locationId = +rec.Id;
        const r = await createLocationStore(loc, self);
        if (!r) continue;
        runInAction(() => {
          self.locationsOrder = [...self.locationsOrder, locationId];
          self.locations = {...self.locations, [locationId]: r};
        })
      }

      runInAction(() => {
        self.isInitialized = true;
      })

      if (active !== undefined && self.locations[active]) {
        await self.setActiveLocation(active);
      }
    }
  };

  return makeObservable(self, {
    isLoading: observable,
    isInitialized: observable,
    locations: observable,
    minersLog: observable,
    activeLocation: observable,
    locationsOrder: observable,
    locationsInOrder:  computed,
    setActiveLocation: action,
    addMinersLog: action,
    showAddLocationModal: action,
    deleteLocation: action,
    addLocation: action,
    checkPositionsOfLocations: action,
    editPositionsOfLocations: action,
  });
};

const createMainLocationsStore = (): MainLocationsStore => createStore((rc) => createMainLocationsStore_(rc));
const mainLocationsStore = createMainLocationsStore();

export default mainLocationsStore;
